import { Helmet } from 'react-helmet-async'

const SITE_NAME = 'RemindLoop'
const SITE_URL = 'https://getremindloop.com'
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`

interface SeoProps {
  title: string
  description: string
  path: string
  /** JSON-LD structured data object(s) -- rendered as <script type="application/ld+json">. */
  jsonLd?: object | object[]
}

/**
 * Per-page <head> management for the public marketing site -- title,
 * description, canonical URL, and Open Graph/Twitter tags, plus optional
 * JSON-LD structured data. react-helmet-async also lets the same tags get
 * collected server-side during prerendering (see entry-server.tsx) via
 * HelmetProvider's context, not just client-side -- a plain document.title
 * mutation wouldn't be visible to a crawler reading prerendered HTML.
 */
export function Seo({ title, description, path, jsonLd }: SeoProps) {
  // react-helmet-async's server-side context extraction doesn't populate
  // reliably with this React version (verified independently -- it also
  // leaks title/meta/script tags inline into the rendered body rather
  // than lifting them to <head>). The build-time prerender step
  // (entry-server.tsx/scripts/prerender.mjs) builds <head> content
  // directly from each page's exported seo* constants instead, so this
  // component only needs to do anything in a real browser.
  if (typeof document === 'undefined') return null

  const url = `${SITE_URL}${path}`
  const fullTitle = path === '/' ? title : `${title} | ${SITE_NAME}`
  const jsonLdBlocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />

      {jsonLdBlocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  )
}

export { SITE_NAME, SITE_URL }
